import * as moment from 'moment';

import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { auditLogAdapter } from '../shared-audit.adapters';
import * as actions from './staff-event.actions';
import {
  StaffEventSkeleton,
  StaffEventAudit,
  StaffEventBusinessPersonAudit,
  ScheduledEventAudit
} from '../../+models/staff-event-audit.model';

//States
interface StaffEventSkeletonState extends EntityState<StaffEventSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface StaffEventAuditState extends EntityState<StaffEventAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface StaffEventBusinessPersonAuditState extends EntityState<StaffEventBusinessPersonAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ScheduledEventAuditState extends EntityState<ScheduledEventAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface StaffEventTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loading: boolean;
  loaded: boolean;
}

function sortStaffEvents(a: StaffEventSkeleton, b: StaffEventSkeleton) {
  const eventTypeCompareResult = a.staffEventType.localeCompare(b.staffEventType);
  if (eventTypeCompareResult !== 0) {
    return eventTypeCompareResult;
  }
  const aStartDateTimeUtc = moment(a.startDateTimeUTC);
  const bStartDateTimeUtc = moment(b.startDateTimeUTC);
  if (aStartDateTimeUtc.isBefore(bStartDateTimeUtc)) {
    return -1;
  } else if (aStartDateTimeUtc.isSame(bStartDateTimeUtc)) {
    return 0;
  } else {
    return 1;
  }
}

// Adapters
const staffEventSkeletonAdapter = createEntityAdapter<StaffEventSkeleton>({
  selectId: p => p.staffEventId,
  sortComparer: sortStaffEvents
});

const staffEventTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

// Entity Selectors
export const staffEventSkeletonSelectors = staffEventSkeletonAdapter.getSelectors();
export const staffEventAuditSelectors = auditLogAdapter.getSelectors();
export const staffEventBusinessPersonAuditSelectors = auditLogAdapter.getSelectors();
export const scheduledEventAuditSelectors = auditLogAdapter.getSelectors();
export const staffEventTypeSelectors = staffEventTypeAdapter.getSelectors();

// Initial States
const staffEventSkeletonInitialState = staffEventSkeletonAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const staffEventAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const staffEventBusinessPersonAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const scheduledEventAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const staffEventTypeInitialState = staffEventTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

export interface State {
  staffEventSkeletons: StaffEventSkeletonState;
  staffEventAudit: StaffEventAuditState;
  staffEventBusinessPersonAudit: StaffEventBusinessPersonAuditState;
  scheduledEventAudit: ScheduledEventAuditState;
  staffEventType: StaffEventTypeState;
}

const initialState: State = {
  staffEventSkeletons: staffEventSkeletonInitialState,
  staffEventAudit: staffEventAuditInitialState,
  staffEventBusinessPersonAudit: staffEventBusinessPersonAuditInitialState,
  scheduledEventAudit: scheduledEventAuditInitialState,
  staffEventType: staffEventTypeInitialState
};

const staffEventReducer = createReducer<State>(
  initialState,
  on(actions.ResetStaffEvent, () => initialState),

  on(actions.LoadStaffEventSkeletons, state => ({
    ...state,

    staffEventSkeletons: {
      ...state.staffEventSkeletons,
      loading: true
    }
  })),

  on(actions.StaffEventSkeletonsLoaded, (state, action) => ({
    ...state,

    staffEventSkeletons: {
      ...state.staffEventSkeletons,
      ...staffEventSkeletonAdapter.setAll(action.payload, state.staffEventSkeletons),
      loading: false,
      loaded: true
    }
  })),

  on(actions.LoadStaffEventAudit, (state, action) => ({
    ...state,

    staffEventAudit: {
      ...state.staffEventAudit,
      lastAuditLogId:
        state.staffEventAudit.selectedId === action.staffEventId
          ? state.staffEventAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.StaffEventAuditLoaded, (state, action) => ({
    ...state,

    staffEventAudit: {
      ...state.staffEventAudit,
      ...(!state.staffEventAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.staffEventAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.staffEventAudit)),
      loaded: true,
      loading: false,
      selectedId: action.staffEventId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadStaffEventBusinessPersonAudit, (state, action) => ({
    ...state,

    staffEventBusinessPersonAudit: {
      ...state.staffEventBusinessPersonAudit,
      lastAuditLogId:
        state.staffEventBusinessPersonAudit.selectedId === action.staffEventBusinessPersonId
          ? state.staffEventBusinessPersonAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.StaffEventBusinessPersonAuditLoaded, (state, action) => ({
    ...state,

    staffEventBusinessPersonAudit: {
      ...state.staffEventBusinessPersonAudit,
      ...(!state.staffEventBusinessPersonAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.staffEventBusinessPersonAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.staffEventBusinessPersonAudit)),
      loaded: true,
      loading: false,
      selectedId: action.staffEventBusinessPersonId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadScheduledEventAudit, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      lastAuditLogId:
        state.scheduledEventAudit.selectedId === action.scheduledEventId
          ? state.scheduledEventAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ScheduledEventAuditLoaded, (state, action) => ({
    ...state,

    scheduledEventAudit: {
      ...state.scheduledEventAudit,
      ...(!state.scheduledEventAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.scheduledEventAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.scheduledEventAudit)),
      loaded: true,
      loading: false,
      selectedId: action.scheduledEventId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadStaffEventTypes, state => ({
    ...state,

    staffEventType: {
      ...state.staffEventType,
      loading: true
    }
  })),

  on(actions.StaffEventTypesLoaded, (state, action) => ({
    ...state,

    staffEventType: {
      ...state.staffEventType,
      ...staffEventTypeAdapter.setAll(action.payload, state.staffEventType),
      loaded: true,
      loading: false
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return staffEventReducer(state, action);
}
